/**
 * Relative date-range presets — the "Last 7 days" / "Last 30 days" / …
 * option list and the day/month math behind it.
 *
 * Shared by RelativeDateRangePicker (the general-purpose date-range field)
 * and any caller that needs the same relative-range vocabulary without the
 * full picker UI (e.g. a single-select quick-filter chip).
 *
 * Contains:
 * - getDefaultDateRangePresets — translated { value, label, days|months } list
 * - calculatePresetRange — preset -> { start, end } Date objects
 */
import { endOfDay, startOfDay, subDays, subMonths, subYears } from 'date-fns';

/**
 * Build the default preset option list, translated via the given `t`.
 * Callers that only need a subset can filter the returned array by `value`.
 */
export function getDefaultDateRangePresets(t) {
  return [
    { value: 'today', label: t('dateRange.today', { defaultValue: 'Today' }), days: 0 },
    {
      value: 'yesterday',
      label: t('dateRange.yesterday', { defaultValue: 'Yesterday' }),
      days: 1,
      isYesterday: true,
    },
    { value: 'last7days', label: t('dateRange.last7days', { defaultValue: 'Last 7 days' }), days: 7 },
    { value: 'last14days', label: t('dateRange.last14days', { defaultValue: 'Last 14 days' }), days: 14 },
    { value: 'last30days', label: t('dateRange.last30days', { defaultValue: 'Last 30 days' }), days: 30 },
    { value: 'last60days', label: t('dateRange.last60days', { defaultValue: 'Last 60 days' }), days: 60 },
    { value: 'last90days', label: t('dateRange.last90days', { defaultValue: 'Last 90 days' }), days: 90 },
    { value: 'last6months', label: t('dateRange.last6months', { defaultValue: 'Last 6 months' }), months: 6 },
    {
      value: 'last12months',
      label: t('dateRange.last12months', { defaultValue: 'Last 12 months' }),
      months: 12,
    },
    { value: 'lastyear', label: t('dateRange.lastYear', { defaultValue: 'Last year' }), years: 1 },
  ];
}

/**
 * Resolve a preset descriptor ({ days|months|years|isYesterday }) to a
 * { start, end } Date range. `includeTime: false` (the default) strips the
 * time component down to the start of each day.
 */
export function calculatePresetRange(preset, { includeTime = false } = {}) {
  const now = new Date();
  let start;
  let end;

  if (preset.isYesterday) {
    start = startOfDay(subDays(now, 1));
    end = endOfDay(subDays(now, 1));
  } else if (preset.days !== undefined) {
    if (preset.days === 0) {
      start = startOfDay(now);
      end = endOfDay(now);
    } else {
      start = startOfDay(subDays(now, preset.days - 1));
      end = endOfDay(now);
    }
  } else if (preset.months !== undefined) {
    start = startOfDay(subMonths(now, preset.months));
    end = endOfDay(now);
  } else if (preset.years !== undefined) {
    start = startOfDay(subYears(now, preset.years));
    end = endOfDay(now);
  }

  if (!includeTime) {
    start = startOfDay(start);
    end = startOfDay(end);
  }

  return { start, end };
}
